import { createContext, useContext } from "react";
import { CommandDropdownContextValue, LevelContextValue, Path } from "./types";

// ============ Main Dropdown Context ============
const defaultContextValue: CommandDropdownContextValue = {
    searchValue: "",
    setSearchValue: () => {},
    filterMode: "none",
    isOpen: false,
    setIsOpen: () => {},
    activePath: [],
    setActivePath: () => {},
    activeIndex: 0,
    setActiveIndex: () => {},
    enableKeyboard: true,
    // Drill-down navigation
    navigationStack: [],
    pushNavigation: () => {},
    popNavigation: () => {},
    resetNavigation: () => {},
    navigateToIndex: () => {},
    currentNavigation: null,
    initializeNavigationForSelection: () => false,
    registerLabel: () => {},
    getLabelForPath: () => undefined,
    updateNavigationLabel: () => {},
    registerPathForId: () => {},
    getPathForId: () => undefined,
    registerItemForPath: () => {},
    getItemForPath: () => undefined,
    // Selection - now path-based
    selections: [],
    multiple: false,
    onItemSelect: () => {},
    removeSelection: () => {},
    itemCount: 0,
    registerItems: () => {},
    isLoading: false,
    setIsLoading: () => {},
    // Keyboard item action registration
    registerItemAction: () => {},
    clearItemActions: () => {},
    triggerActiveItem: () => {},
};

export const CommandDropdownContext =
    createContext<CommandDropdownContextValue>(defaultContextValue);

export function useCommandDropdown() {
    const context = useContext(CommandDropdownContext);
    if (!context) {
        throw new Error("useCommandDropdown must be used within a BaseCommandDropdown");
    }
    return context;
}

// ============ Level Context (for depth tracking) ============
const defaultLevelContextValue: LevelContextValue = {
    depth: 0,
    parentPath: [],
};

export const LevelContext = createContext<LevelContextValue>(defaultLevelContextValue);

export function useLevelContext() {
    return useContext(LevelContext);
}

// ============ Search Filtering Utility ============
export function matchesSearch(
    searchValue: string,
    label: React.ReactNode,
    searchKeywords?: string[]
): boolean {
    if (!searchValue.trim()) return true;

    const normalizedSearch = searchValue.toLowerCase().trim();

    const fuzzyMatch = (haystack: string): boolean => {
        // Subsequence match: "aple" matches "apple"
        let i = 0;
        for (let j = 0; j < haystack.length && i < normalizedSearch.length; j += 1) {
            if (haystack[j] === normalizedSearch[i]) {
                i += 1;
            }
        }
        return i === normalizedSearch.length;
    };

    const matchesValue = (value?: string): boolean => {
        if (!value) return false;
        const normalized = value.toLowerCase();
        return normalized.includes(normalizedSearch) || fuzzyMatch(normalized);
    };

    if (searchKeywords?.some((kw) => matchesValue(kw))) {
        return true;
    }

    if (typeof label === "string" && matchesValue(label)) {
        return true;
    }

    return false;
}

// ============ Path Comparison Utility ============
export function pathsEqual(a: Path, b: Path): boolean {
    if (a.length !== b.length) return false;
    return a.every((key, index) => key === b[index]);
}

// ============ Path Key Utility ============
export function pathToKey(path: Path): string {
    return JSON.stringify(path);
}
